// Calcul des frais de déplacement d'une antenne pour un devis.
//
// Règle donnée par les commerciaux (2026-10) :
// - un devis compte en général 2 trajets : l'aller (livraison) et le retour (reprise) ;
// - le forfait de la grille (40 €) est le prix de l'ALLER-RETOUR complet : il couvre
//   les premiers km (80), puis le barème au km (0,70 €) au-delà ; chaque trajet
//   (aller, retour) en coûte la moitié : 20 € + 20 € (précision commerciale 2026-11) ;
// - un aller-retour la nuit, un dimanche ou un jour férié passe au forfait
//   majoré de la grille, s'il existe : les nouvelles antennes n'en ont plus ;
// - les péages s'ajoutent éventuellement.
// Les montants viennent de la grille de l'antenne, les règles horaires du
// paramètre `tarifs.regles_deplacement` (page Tarifs) : tout reste modifiable
// dans le calculateur, la règle n'est qu'un point de départ.

export const REGLES_CLE = 'tarifs.regles_deplacement';

export const REGLES_DEFAUT = {
  nuit_debut: '21:00',
  nuit_fin: '06:00',
  samedi: false,
  dimanche: true,
  feries: true,
  km_inclus: 80,
  // 'ar' : seuil comparé à la distance aller-retour ; 'aller' : à l'aller simple
  seuil_sur: 'ar',
};

export function lireRegles(valeur) {
  return { ...REGLES_DEFAUT, ...(valeur && typeof valeur === 'object' ? valeur : {}) };
}

// Dimanche de Pâques (algorithme de Meeus / Jones / Butcher)
function paques(annee) {
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31);
  const jour = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(annee, mois - 1, jour);
}

const pad = (n) => String(n).padStart(2, '0');
const cle = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Jours fériés de France métropolitaine, nommés
export function joursFeries(annee) {
  const p = paques(annee);
  const decale = (n) => { const d = new Date(p); d.setDate(d.getDate() + n); return d; };
  const fixes = [
    ['01-01', "Jour de l'an"], ['05-01', 'Fête du travail'], ['05-08', 'Victoire 1945'],
    ['07-14', 'Fête nationale'], ['08-15', 'Assomption'], ['11-01', 'Toussaint'],
    ['11-11', 'Armistice'], ['12-25', 'Noël'],
  ];
  const m = new Map(fixes.map(([md, nom]) => [`${annee}-${md}`, nom]));
  m.set(cle(decale(1)), 'Lundi de Pâques');
  m.set(cle(decale(39)), 'Ascension');
  m.set(cle(decale(50)), 'Lundi de Pentecôte');
  return m;
}

const minutes = (hhmm) => {
  const [h, mi] = String(hhmm || '0:0').split(':').map((x) => parseInt(x, 10) || 0);
  return h * 60 + mi;
};

// Raison de la majoration pour un départ donné, ou null.
// `quand` : 'AAAA-MM-JJTHH:mm' (heure locale, comme un champ datetime-local).
export function raisonMajoration(quand, regles) {
  if (!quand || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(quand)) return null;
  const [y, mo, j] = quand.slice(0, 10).split('-').map(Number);
  const d = new Date(y, mo - 1, j);
  const ferie = joursFeries(y).get(quand.slice(0, 10));
  if (regles.feries && ferie) return `jour férié (${ferie})`;
  if (regles.dimanche && d.getDay() === 0) return 'dimanche';
  if (regles.samedi && d.getDay() === 6) return 'samedi';
  const t = minutes(quand.slice(11, 16));
  const deb = minutes(regles.nuit_debut);
  const fin = minutes(regles.nuit_fin);
  if (deb !== fin) {
    // Plage qui passe minuit (21:00 → 06:00) ou non (00:00 → 06:00)
    const nuit = deb > fin ? (t >= deb || t < fin) : (t >= deb && t < fin);
    if (nuit) return `nuit (${regles.nuit_debut} – ${regles.nuit_fin})`;
  }
  return null;
}

const num = (v) => (Number.isFinite(+v) ? +v : 0);

// Paramètres de départ tirés de la grille de l'antenne.
// Le forfait majoré dépend du type de borne et du trajet : livraison pour le
// premier aller-retour, reprise (retour) pour le second.
export function parametresDepuisGrille(grille, regles) {
  const l = grille?.livraison || {};
  return {
    forfait: num(l.forfait_min_km),
    km_inclus: num(regles.km_inclus),
    bareme_km: num(l.bareme_km),
    majore: {
      aller: l.forfait_aller_majore || {},
      retour: l.forfait_retour_majore || {},
    },
  };
}

// Prix d'un aller-retour au tarif normal, pour une distance aller simple (km).
export function prixNormal(kmAller, p, seuilSur) {
  const kmFactures = seuilSur === 'aller'
    ? Math.max(0, kmAller - p.km_inclus) * 2
    : Math.max(0, kmAller * 2 - p.km_inclus);
  return { kmFactures, montant: p.forfait + kmFactures * p.bareme_km };
}

// Calcul complet. `allersRetours` : [{ libelle, trajet: 'aller'|'retour', majore: bool }]
// Chaque ligne est un trajet (aller ou retour) : la moitié du prix normal de l'aller-retour.
// Les forfaits majorés de la grille sont déjà donnés par trajet (aller, retour).
export function calculerFrais({ kmAller, peageAller, avecPeages, allersRetours, p, borne, seuilSur }) {
  const lignes = allersRetours.map((ar) => {
    const forfaitMajore = num(p.majore[ar.trajet]?.[borne]);
    const allerRetour = prixNormal(kmAller, p, seuilSur);
    const normal = { kmFactures: allerRetour.kmFactures / 2, montant: allerRetour.montant / 2 };
    // Sans forfait majoré dans la grille (nouvelles antennes), on reste au normal.
    const majoreApplique = ar.majore && forfaitMajore > 0;
    const deplacement = majoreApplique ? forfaitMajore : normal.montant;
    const peages = avecPeages && peageAller != null ? peageAller * 2 : 0;
    return {
      ...ar,
      majoreApplique,
      majoreSansForfait: ar.majore && !(forfaitMajore > 0),
      kmFactures: normal.kmFactures,
      deplacement,
      peages,
      total: deplacement + peages,
    };
  });
  return { lignes, total: lignes.reduce((s, l) => s + l.total, 0) };
}
